import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import type React from "react"

import type { RunRowLike } from "@/types/RunRow"

/**
 * The table's row-selection ids, or `"all"` for every selectable row. `"all"`
 * is expanded to the concrete ids on the next render.
 */
type RunResultsSelection = Set<string> | "all"

const areStringArraysEqual = (left: string[], right: string[]) => {
  if (left.length !== right.length) {
    return false
  }

  return left.every((value, index) => value === right[index])
}

const areStringSetsEqual = (
  left: Set<string>,
  rightValues: Iterable<string>,
) => {
  const right = new Set(rightValues)

  if (left.size !== right.size) {
    return false
  }

  for (const value of left) {
    if (!right.has(value)) {
      return false
    }
  }

  return true
}

interface UseRunResultsSelectionParams {
  scopedRunRows: RunRowLike[]
  onSelectionChange?: (selectedIds: string[]) => void
}

interface RunResultsSelectionController {
  selectedKeys: RunResultsSelection
  setSelectedKeys: React.Dispatch<React.SetStateAction<RunResultsSelection>>
  isSelectMode: boolean
  setIsSelectMode: React.Dispatch<React.SetStateAction<boolean>>
  selectableSimulationOutputIds: string[]
  selectableSimulationOutputIdSet: Set<string>
  selectedSimulationOutputIds: string[]
  /** Distinct test cases behind the selected rows. */
  selectedTestCaseIds: string[]
  selectedCount: number
  handleClearSelection: () => void
}

/**
 * Owns multi-select state for the run results table: which rows are selectable
 * (derived from the scoped rows), the current selection, select-mode toggle, and
 * keeping the selection pruned to selectable ids while notifying the parent when
 * it changes.
 */
export function useRunResultsSelection({
  scopedRunRows,
  onSelectionChange,
}: UseRunResultsSelectionParams): RunResultsSelectionController {
  const [selectedKeys, setSelectedKeys] = useState<RunResultsSelection>(
    new Set<string>(),
  )
  const [isSelectMode, setIsSelectMode] = useState(false)

  const selectableSimulationOutputIds = useMemo(
    () =>
      scopedRunRows
        .map((row) => row.simulation_output_id)
        .filter(
          (simulationOutputId): simulationOutputId is string =>
            typeof simulationOutputId === "string" &&
            simulationOutputId.length > 0,
        ),
    [scopedRunRows],
  )

  const selectableSimulationOutputIdSet = useMemo(
    () => new Set(selectableSimulationOutputIds),
    [selectableSimulationOutputIds],
  )

  const selectedSimulationOutputIds = useMemo(() => {
    if (selectedKeys === "all") {
      return selectableSimulationOutputIds
    }

    return Array.from(selectedKeys).filter(
      (key) =>
        selectableSimulationOutputIdSet.has(key) && !key.startsWith("run-"),
    )
  }, [
    selectableSimulationOutputIdSet,
    selectableSimulationOutputIds,
    selectedKeys,
  ])

  const selectedTestCaseIds = useMemo(() => {
    const selectedIdSet = new Set(selectedSimulationOutputIds)
    const ids = new Set<string>()
    for (const row of scopedRunRows) {
      if (selectedIdSet.has(row.simulation_output_id) && row.test_set_row_id) {
        ids.add(row.test_set_row_id)
      }
    }
    return [...ids]
  }, [scopedRunRows, selectedSimulationOutputIds])

  const previousSelectedSimulationOutputIdsRef = useRef<string[] | null>(null)

  useEffect(() => {
    if (selectedKeys === "all") {
      setSelectedKeys(new Set(selectableSimulationOutputIds))
      return
    }

    const nextSelectedIds = Array.from(selectedKeys).filter(
      (key) =>
        selectableSimulationOutputIdSet.has(key) && !key.startsWith("run-"),
    )

    if (!areStringSetsEqual(selectedKeys, nextSelectedIds)) {
      setSelectedKeys(new Set(nextSelectedIds))
    }
  }, [
    selectableSimulationOutputIdSet,
    selectableSimulationOutputIds,
    selectedKeys,
  ])

  useEffect(() => {
    if (!onSelectionChange) {
      return
    }

    if (
      previousSelectedSimulationOutputIdsRef.current &&
      areStringArraysEqual(
        previousSelectedSimulationOutputIdsRef.current,
        selectedSimulationOutputIds,
      )
    ) {
      return
    }

    previousSelectedSimulationOutputIdsRef.current = selectedSimulationOutputIds
    onSelectionChange(selectedSimulationOutputIds)
  }, [onSelectionChange, selectedSimulationOutputIds])

  const handleClearSelection = useCallback(() => {
    setSelectedKeys(new Set<string>())
    setIsSelectMode(false)
  }, [])

  const selectedCount = useMemo(() => {
    return selectedSimulationOutputIds.length
  }, [selectedSimulationOutputIds])

  return {
    selectedKeys,
    setSelectedKeys,
    isSelectMode,
    setIsSelectMode,
    selectableSimulationOutputIds,
    selectableSimulationOutputIdSet,
    selectedSimulationOutputIds,
    selectedTestCaseIds,
    selectedCount,
    handleClearSelection,
  }
}
